import React, {useMemo} from 'react'
import parse from 'html-react-parser'
import PropTypes from 'prop-types'
import {useContentSlots} from '../../hooks/use-content-slot'
import {useSiteCustomPreferences} from '../../hooks/use-site-custom-preferences'
import useSlotCalloutMessage from '../../hooks/use-slot-callout-message'
import TAContentSlot from './think-anlytics-slot'
import ProductCarousel from '../../components/product-carousel'

const extractEinsteinVariables = (html) => {
    if (!html) return null

    const cleaned = html
        .replace(/\\x3C/g, '<')
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\"/g, '"')
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&#39;/g, "'")

    const recommenderRegex = /var\s+recommender\s*=\s*['"`]?\[\[\s*"?([^\]"']+)"?\s*\]\]['"`]?;/
    const slotUUIDRegex = /var\s+slotConfigurationUUID\s*=\s*['"`]?([^'"`;]+)['"`]?;/
    const slotNameRegex = /var\s+slotName\s*=\s*decodeHtml\(['"`]([^'"`]+)['"`]\);/
    const configIdRegex = /var\s+slotConfigId\s*=\s*decodeHtml\(['"`]([^'"`]+)['"`]\);/
    const templateRegex = /var\s+slotConfigTemplate\s*=\s*decodeHtml\(['"`]([^'"`]+)['"`]\);/
    const typeRegex = /var\s+slotRecommendationType\s*=\s*decodeHtml\(['"`]([^'"`]+)['"`]\);/

    const recommenderMatch = cleaned.match(recommenderRegex)
    const uuidMatch = cleaned.match(slotUUIDRegex)
    const nameMatch = cleaned.match(slotNameRegex)
    const configIdMatch = cleaned.match(configIdRegex)
    const templateMatch = cleaned.match(templateRegex)
    const typeMatch = cleaned.match(typeRegex)

    const recommender = recommenderMatch?.[1]?.trim()
    if (!recommender) return null

    return {
        recommender,
        slotRecommendationType: typeMatch?.[1]?.trim(),
        slotConfigurationUUID: uuidMatch?.[1]?.trim(),
        slotName: nameMatch?.[1]?.trim(),
        slotConfigId: configIdMatch?.[1]?.trim(),
        slotConfigTemplate: templateMatch?.[1]?.trim()
    }
}

const EinsteinTaSlotHtml = ({slotId, context, type}) => {
    const {data: slotData} = useContentSlots(slotId, context, type)
    const parsedHtml = slotData ? parse(slotData) : null
    const {data: calloutMessage, isLoading} = useSlotCalloutMessage(slotId, context)
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const isTAEnabled = getcustomPreferencevalue('pwaThinkAnalyticsEnabled')
    let contentSlot = null
    let isTAContent = false

    // Check for TA (Think Analytics)
    if (typeof parsedHtml === 'string' && parsedHtml.includes('isTA') && isTAEnabled) {
        try {
            contentSlot = JSON.parse(parsedHtml)
            isTAContent = !!contentSlot?.isTA
        } catch (e) {
            console.error('[EinsteinTaSlotHtml] Invalid TA JSON:', e)
        }
    }

    // Extract Einstein config only if it's NOT TA
    const einsteinMITConfig = useMemo(() => {
        if (typeof slotData === 'string' && !isTAContent) {
            return extractEinsteinVariables(slotData)
        }
        return null
    }, [slotData, isTAContent])

    if (isTAContent) {
        return (
            <TAContentSlot
                title="ThinkAnalytics"
                contentSlot={contentSlot}
                enableFavourite={true}
            />
        )
    }

    // Default to "You May Also Like" if message not loaded yet
    const title =
        !isTAContent && !isLoading && calloutMessage ? calloutMessage : 'You May Also Like'

    if (einsteinMITConfig?.recommender) {
        const isRecentlyViewed =
            typeof einsteinMITConfig?.slotConfigTemplate === 'string' &&
            einsteinMITConfig.slotConfigTemplate.includes('showOnlyNameAndImage')
        return (
            <ProductCarousel
                title={title}
                recommender={einsteinMITConfig.recommender}
                isProductTileRecentlyViewed={isRecentlyViewed}
            />
        )
    }

    return null
}

EinsteinTaSlotHtml.propTypes = {
    slotId: PropTypes.string.isRequired,
    context: PropTypes.string,
    type: PropTypes.string
}

export default EinsteinTaSlotHtml
